import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_url: string | null;
  landing_page: string | null;
}

/**
 * Captures UTM parameters from the current URL and referrer.
 * Used on all public forms to track lead source attribution.
 */
export function useUTMParams(): UTMParams {
  const [searchParams] = useSearchParams();

  return useMemo(() => ({
    utm_source: searchParams.get('utm_source'),
    utm_medium: searchParams.get('utm_medium'),
    utm_campaign: searchParams.get('utm_campaign'),
    referrer_url: typeof document !== 'undefined' ? document.referrer || null : null,
    landing_page: typeof window !== 'undefined' ? window.location.pathname : null,
  }), [searchParams]);
}

/**
 * Get stored UTM params from sessionStorage (persists across page navigation)
 */
export function getStoredUTMParams(): UTMParams {
  if (typeof window === 'undefined') {
    return { utm_source: null, utm_medium: null, utm_campaign: null, referrer_url: null, landing_page: null };
  }

  try {
    const stored = sessionStorage.getItem('car_street_utm');
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }

  return { utm_source: null, utm_medium: null, utm_campaign: null, referrer_url: null, landing_page: null };
}

/**
 * Store UTM params in sessionStorage on first visit
 */
export function storeUTMParams(params: UTMParams): void {
  if (typeof window === 'undefined') return;
  // Only store if we have at least one UTM param and haven't stored yet
  if (params.utm_source || params.utm_medium || params.utm_campaign) {
    const existing = sessionStorage.getItem('car_street_utm');
    if (!existing) {
      sessionStorage.setItem('car_street_utm', JSON.stringify(params));
    }
  }
}

/**
 * Infer lead source from UTM params
 */
export function inferLeadSource(params: UTMParams): string {
  if (params.utm_source) {
    const src = params.utm_source.toLowerCase();
    if (src.includes('google')) return 'google_ads';
    if (src.includes('facebook') || src.includes('fb')) return 'facebook';
    if (src.includes('instagram') || src.includes('ig')) return 'instagram';
    if (src.includes('autotrader')) return 'autotrader';
    if (src.includes('kijiji')) return 'kijiji';
  }
  if (params.referrer_url) {
    const ref = params.referrer_url.toLowerCase();
    if (ref.includes('google')) return 'google_ads';
    if (ref.includes('facebook')) return 'facebook';
    if (ref.includes('instagram')) return 'instagram';
  }
  return 'website';
}
